import { type GeneralLink } from "@/types"

/**
 * Generates a href value for a given GeneralLink object.
 *
 * The function supports constructing hrefs for different link types: external URLs, internal paths,
 * category URIs, and product display URIs. It prioritizes these properties in the order listed.
 * If the link is external, it returns the external URL directly. For internal links, category URIs,
 * and product display URIs, it ensures the returned href starts with a slash ("/"). If none of these
 * properties are defined, it returns undefined.
 *
 * @param {GeneralLink} link The link object containing properties that dictate the href's structure.
 * @returns {string | undefined} The constructed href based on the link object properties, or undefined if no valid properties are provided.
 *
 * @deprecated Use `getGCGeneralLinkHref` instead.
 *
 * @example
 * const link = { external: "https://example.com" };
 * const href = getGeneralLinkHref(link);
 * // Returns 'https://example.com'
 */
export function getGeneralLinkHref(link?: GeneralLink): string | undefined {
    if (!link) return undefined
    const href =
        link.external ||
        link.internal ||
        (link.newCategoryUri && `/categories/${link.newCategoryUri}`) ||
        (link.categoryUri && `/categories/${link.categoryUri}`) ||
        (link.productDisplayUri && `/product/${link.productDisplayUri}`) ||
        (link.gcProductVariantUri && `/product/${link.gcProductVariantUri}`) ||
        (link.gcProductVariantId && `/product/${link.gcProductVariantId}`) ||
        undefined

    // Ensure href is a string before proceeding.
    if (typeof href === "undefined" || typeof href !== "string") return href

    if (link.external) return href

    return href.startsWith("/") ? href : `/${href}`
}
